import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Linkedin, Link as LinkIcon, BookOpen, BarChart3, Quote, ExternalLink, Loader2 } from 'lucide-react';
import SEO from '@/components/SEO';
import BLOG_POSTS_CONTENT from '@/data/blogPostsContent';

const BlogPostPage = () => {
  const { postSlug } = useParams();
  const [copied, setCopied] = useState(false);
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch blog post from API with fallback to local data
  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/blog/posts/full/${postSlug}`);
        if (response.ok) {
          const data = await response.json();
          // Transform API data to match expected format
          setPost({
            ...data,
            keyStats: data.key_stats || data.keyStats,
            pullQuote: data.pull_quote || data.pullQuote,
            readTime: data.read_time || data.readTime,
            authorTitle: data.author_title || data.authorTitle,
          });
        } else {
          // Fallback to local data
          setPost(BLOG_POSTS_CONTENT[postSlug]);
        }
      } catch (error) {
        console.log('Using local blog data as fallback');
        setPost(BLOG_POSTS_CONTENT[postSlug]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPost();
  }, [postSlug, API_URL]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] pt-32 pb-20 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-[#C25E3E] animate-spin mx-auto mb-4" />
          <p className="text-[rgba(250,248,244,0.4)]">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] pt-32 pb-20">
        <div className="max-w-[800px] mx-auto px-6 text-center">
          <h1 className="font-display text-4xl font-bold text-sbt-black mb-4">
            Article Not Found
          </h1>
          <p className="font-body text-lg text-sbt-slate mb-8">
            The article you're looking for doesn't exist.
          </p>
          <Link to="/blog" className="btn-primary">
            Back to Journal
          </Link>
        </div>
      </div>
    );
  }

  const PRODUCTION_DOMAIN = 'https://shebuiltthis.co';
  
  // Get share URL for social media (uses API endpoint for proper OG tags)
  const getShareUrl = () => {
    return `${PRODUCTION_DOMAIN}/api/share/blog/${postSlug}`;
  };

  const copyLink = async () => {
    const linkToCopy = getShareUrl();
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(linkToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        fallbackCopyToClipboard(linkToCopy);
      }
    } catch (err) {
      fallbackCopyToClipboard(linkToCopy);
    }
  };

  const fallbackCopyToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      window.prompt('Copy this link:', text);
    }
    document.body.removeChild(textArea);
  };

  const shareLinkedIn = () => {
    const url = encodeURIComponent(getShareUrl());
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank', 'noopener,noreferrer');
  };

  const shareTwitter = () => {
    const text = encodeURIComponent(`${post.title} — Research-backed insights from She Built This`);
    const url = encodeURIComponent(getShareUrl());
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank', 'noopener,noreferrer');
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(`${post.title} — Read this research article from She Built This: ${getShareUrl()}`);
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  // Render content with rich formatting
  const renderContent = (content) => {
    const lines = content.split('\n');
    const elements = [];
    let currentList = [];
    let currentTable = [];
    let inTable = false;

    const flushList = () => {
      if (currentList.length > 0) {
        elements.push(
          <ul key={`list-${elements.length}`} className="list-disc list-outside ml-6 mb-6 space-y-2">
            {currentList.map((item, i) => (
              <li key={i} className="leading-relaxed" style={{ fontFamily: "'Lora', serif", color: 'rgba(250,248,244,0.55)' }}>{item}</li>
            ))}
          </ul>
        );
        currentList = [];
      }
    };

    const flushTable = () => {
      if (currentTable.length > 0) {
        elements.push(
          <div key={`table-${elements.length}`} className="overflow-x-auto mb-8 border border-sbt-border">
            <table className="w-full">
              <tbody>
                {currentTable.map((row, i) => (
                  <tr key={i} className={i === 0 ? 'bg-sbt-green text-white' : i % 2 === 0 ? 'bg-sbt-off-white' : 'bg-white'}>
                    {row.split('|').filter(cell => cell.trim()).map((cell, j) => (
                      i === 0 ? (
                        <th key={j} className="p-4 text-left font-mono text-xs tracking-wide">{cell.trim()}</th>
                      ) : (
                        <td key={j} className="p-4 font-body text-sm text-sbt-slate">{cell.trim()}</td>
                      )
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        currentTable = [];
        inTable = false;
      }
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Table detection
      if (line.includes('|') && line.trim().startsWith('|')) {
        flushList();
        if (line.includes('---') || line.includes('--')) continue;
        currentTable.push(line);
        inTable = true;
        continue;
      } else if (inTable && !line.includes('|')) {
        flushTable();
      }

      // Blockquote
      if (line.startsWith('>')) {
        flushList();
        elements.push(
          <blockquote key={i} className="my-8 p-6 bg-[rgba(201,168,76,0.05)] border-l-4 border-[#C25E3E]">
            <p className="text-xl lg:text-2xl italic leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#F4F0E6' }}>
              {line.replace('> ', '').replace(/\*\*/g, '')}
            </p>
          </blockquote>
        );
        continue;
      }

      // Headers
      if (line.startsWith('## ')) {
        flushList();
        elements.push(
          <h2 key={i} className="text-2xl lg:text-3xl font-bold mt-12 mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#F4F0E6' }}>
            {line.replace('## ', '')}
          </h2>
        );
      } else if (line.startsWith('### ')) {
        flushList();
        elements.push(
          <h3 key={i} className="text-xl font-bold mt-8 mb-4" style={{ fontFamily: "'EB Garamond', serif", color: 'rgba(250,248,244,0.7)' }}>
            {line.replace('### ', '')}
          </h3>
        );
      } else if (line.startsWith('---')) {
        flushList();
        elements.push(
          <div key={i} className="flex items-center justify-center gap-4 my-12">
            <div className="w-12 h-[1px] bg-[#C25E3E]" />
            <div className="w-2 h-2 bg-[#C25E3E] rotate-45" />
            <div className="w-12 h-[1px] bg-[#C25E3E]" />
          </div>
        );
      } else if (line.startsWith('- ') || line.startsWith('* ')) {
        currentList.push(line.replace(/^[-*] /, '').replace(/\*\*/g, ''));
      } else if (line.trim() === '') {
        flushList();
      } else if (line.trim()) {
        flushList();
        const processedLine = line.split(/(\*\*[^*]+\*\*)/).map((part, idx) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={idx} className="font-semibold" style={{ color: '#F4F0E6' }}>{part.slice(2, -2)}</strong>;
          }
          return part;
        });
        elements.push(
          <p key={i} className="text-lg leading-[1.85] mb-6" style={{ fontFamily: "'Lora', serif", color: 'rgba(250,248,244,0.55)' }}>
            {processedLine}
          </p>
        );
      }
    }

    flushList();
    flushTable();

    return elements;
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <SEO 
        title={post.title}
        description={post.subtitle || `Research article by ${post.author} on women in leadership and gender equity.`}
        keywords={[post.category, 'women leadership', 'gender equity research', 'She Built This']}
        url={`https://shebuiltthis.com/blog/${postSlug}`}
        image={post.image}
        type="article"
        author={post.author}
        section={post.category}
      />
      {/* Hero */}
      <section className="relative">
        <div className="w-full h-[40vh] min-h-[300px] max-h-[500px] overflow-hidden">
          <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#080808] to-transparent" />
        </div>
        
        <div className="py-8 border-b border-[rgba(201,168,76,0.1)]">
          <div className="max-w-[900px] mx-auto px-6">
            <Link to="/blog" className="inline-flex items-center gap-2 text-[#C25E3E] hover:underline text-sm mb-6" style={{ fontFamily: "'DM Mono', monospace" }}>
              <ArrowLeft size={16} /> Back to Journal
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 text-[9px] tracking-[0.2em] uppercase border border-[#C25E3E] text-[#C25E3E]" style={{ fontFamily: "'DM Mono', monospace" }}>{post.category}</span>
              <span className="text-xs flex items-center gap-1" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.3)' }}>
                <BookOpen size={12} /> {post.readTime}
              </span>
            </div>
            <h1 className="text-3xl lg:text-5xl font-bold leading-tight mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#F4F0E6' }}>{post.title}</h1>
            {post.subtitle && (
              <p className="text-lg italic mb-6" style={{ fontFamily: "'Lora', serif", color: 'rgba(250,248,244,0.5)' }}>{post.subtitle}</p>
            )}
            <div className="flex items-center gap-4">
              <div>
                <p className="font-bold" style={{ fontFamily: "'EB Garamond', serif", color: '#F4F0E6' }}>{post.author}</p>
                {post.authorTitle && (<p className="text-xs" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.3)' }}>{post.authorTitle}</p>)}
              </div>
              <span className="text-[rgba(250,248,244,0.2)]">&middot;</span>
              <p className="text-sm" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.3)' }}>{post.date}</p>
            </div>
            {post.image && post.image.includes('SBT_') && (
              <div className="mt-6 flex items-center gap-4">
                <a href="https://a.co/d/0jdiF8K1" target="_blank" rel="noopener noreferrer" className="h-11 px-6 text-sm tracking-[0.05em] uppercase flex items-center border border-[#C25E3E] text-[#C25E3E] hover:bg-[#C25E3E] hover:text-[#080808] transition-all" style={{ fontFamily: "'DM Mono', monospace" }}>Buy on Kindle</a>
                <Link to="/book" className="h-11 px-6 text-sm tracking-[0.05em] uppercase flex items-center border border-[#C25E3E] text-[#C25E3E] hover:bg-[#C25E3E] hover:text-[#080808]" style={{ fontFamily: "'DM Mono', monospace" }}>Read Preview</Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Key Stats */}
      {post.keyStats && post.keyStats.length > 0 && (
        <section className="py-8 border-b border-[rgba(201,168,76,0.1)]">
          <div className="max-w-[900px] mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {post.keyStats.map((stat, idx) => (
                <div key={idx} className="text-center">
                  <p className="text-3xl lg:text-4xl font-bold mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#C25E3E' }}>
                    {stat.value}
                  </p>
                  <p className="font-mono text-xs text-sbt-dim uppercase tracking-wide">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Content */}
      <article className="max-w-[720px] mx-auto px-6 py-16">
        {/* Pull Quote */}
        {post.pullQuote && (
          <blockquote className="mb-12 p-8 bg-sbt-green/5 border-l-4 border-sbt-gold">
            <Quote className="text-sbt-gold mb-4" size={32} />
            <p className="font-display text-xl lg:text-2xl italic text-sbt-black leading-relaxed">
              "{post.pullQuote}"
            </p>
          </blockquote>
        )}

        {/* Main Content */}
        <div className="prose prose-lg max-w-none">
          {renderContent(post.content)}
        </div>

        {/* References */}
        {post.references && post.references.length > 0 && (
          <div className="mt-16 pt-8 border-t border-[rgba(201,168,76,0.12)]">
            <h4 className="text-xs tracking-wider uppercase mb-4" style={{ fontFamily: "'DM Mono', monospace", color: '#C25E3E' }}>
              References & Sources
            </h4>
            <div className="space-y-3">
              {post.references.map((ref, idx) => (
                <p key={idx} className="text-xs leading-relaxed pl-4 border-l-2 border-[rgba(201,168,76,0.15)]" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.35)' }}>
                  {ref}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Share Section */}
        <div className="mt-12 pt-8 border-t border-[#C25E3E]">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs tracking-wider uppercase mb-2" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.3)' }}>Share this article</p>
              <div className="flex items-center gap-3 flex-wrap">
                <button onClick={shareLinkedIn} className="flex items-center gap-2 px-4 py-2 bg-[#0077B5] text-white text-xs hover:bg-opacity-90 transition-all" style={{ fontFamily: "'DM Mono', monospace" }} data-testid="share-linkedin"><Linkedin size={14} /> LinkedIn</button>
                <button onClick={shareTwitter} className="flex items-center gap-2 px-4 py-2 bg-[rgba(255,255,255,0.05)] text-[rgba(250,248,244,0.5)] text-xs hover:text-[#C25E3E] transition-all border border-[rgba(201,168,76,0.08)]" style={{ fontFamily: "'DM Mono', monospace" }} data-testid="share-twitter"><ExternalLink size={14} /> X</button>
                <button onClick={shareWhatsApp} className="flex items-center gap-2 px-4 py-2 bg-[#25D366] text-white text-xs hover:bg-opacity-90 transition-all" style={{ fontFamily: "'DM Mono', monospace" }} data-testid="share-whatsapp"><ExternalLink size={14} /> WhatsApp</button>
                <button onClick={copyLink} className="flex items-center gap-2 px-4 py-2 border border-[rgba(250,248,244,0.15)] text-[rgba(250,248,244,0.5)] text-xs hover:border-[#C25E3E] transition-all" style={{ fontFamily: "'DM Mono', monospace" }} data-testid="copy-link"><LinkIcon size={14} /> {copied ? 'Copied!' : 'Copy Link'}</button>
              </div>
            </div>
            <Link to="/blog" className="btn-secondary">More Articles</Link>
          </div>
        </div>
      </article>

      {/* Author Bio */}
      <section className="py-16 border-t border-[rgba(201,168,76,0.08)]">
        <div className="max-w-[720px] mx-auto px-6 text-center">
          <p className="text-xs tracking-wider uppercase mb-4" style={{ fontFamily: "'DM Mono', monospace", color: '#C25E3E' }}>About the Author</p>
          <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#F4F0E6' }}>{post.author}</h3>
          <p className="leading-relaxed mb-6" style={{ fontFamily: "'Lora', serif", color: 'rgba(250,248,244,0.4)' }}>
            {post.author === 'Dr. Neha Dewan' 
              ? 'Founder of She Built This. Co-Founder & CEO of SKI APAC FZCO. Her research — a nine-framework systematic analysis of fifty years of evidence on women in leadership — is currently under review at SAGE Open journal.'
              : 'The She Built This Editorial team researches, verifies, and documents the stories of women who built.'
            }
          </p>
          {post.author === 'Dr. Neha Dewan' && (
            <Link to="/profiles/dr-neha-dewan" className="text-sm text-[#C25E3E] hover:text-[#1A1A1A] transition-colors" style={{ fontFamily: "'DM Mono', monospace" }}>Read her profile →</Link>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogPostPage;
